import { useCallback } from 'react'
import api from '@/lib/api'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { useAgentBuilder } from './useAgentBuilder'
import BuilderPhaseUrl from './BuilderPhaseUrl'
import BuilderPhaseDiscovery from './BuilderPhaseDiscovery'
import BuilderPhaseQuestions from './BuilderPhaseQuestions'
import BuilderPhaseOperations from './BuilderPhaseOperations'
import BuilderPhaseKnowledge from './BuilderPhaseKnowledge'
import BuilderPhaseVoice from './BuilderPhaseVoice'
import BuilderPhasePhone from './BuilderPhasePhone'
import BuilderPhaseReview from './BuilderPhaseReview'
import type { BuilderPhase } from './types'

const COMMON_TZ_LABELS: Record<string, string> = {
  'America/New_York': 'Eastern',
  'America/Chicago': 'Central',
  'America/Denver': 'Mountain',
  'America/Los_Angeles': 'Pacific',
  'America/Phoenix': 'Arizona',
  'America/Anchorage': 'Alaska',
  'Pacific/Honolulu': 'Hawaii',
}

interface AgentBuilderProps {
  open: boolean
  onClose: () => void
  onCreated: () => void
  mode?: 'create' | 'configure'  // 'configure' skips phone + review, just saves config
}

const CREATE_PHASES: BuilderPhase[] = ['url', 'discovery', 'questions', 'operations', 'knowledge', 'voice', 'phone', 'review']
const CONFIGURE_PHASES: BuilderPhase[] = ['url', 'discovery', 'questions', 'operations', 'knowledge', 'voice']

const PHASE_LABELS: Record<BuilderPhase, string> = {
  url: 'Website',
  discovery: 'Discovery',
  questions: 'Firm Details',
  operations: 'Operations',
  knowledge: 'Knowledge',
  voice: 'Voice',
  phone: 'Phone',
  review: 'Create',
}

export default function AgentBuilder({ open, onClose, onCreated, mode = 'create' }: AgentBuilderProps) {
  const { toast } = useToast()
  const { state, dispatch, startScrape, generateGreeting, createAgents } = useAgentBuilder(open, mode)

  const PHASE_ORDER = mode === 'configure' ? CONFIGURE_PHASES : CREATE_PHASES

  const goToPhase = useCallback((phase: BuilderPhase) => {
    dispatch({ type: 'SET_PHASE', phase })
  }, [dispatch])

  const advancePhase = useCallback((currentPhase: BuilderPhase) => {
    dispatch({ type: 'COMPLETE_PHASE', phase: currentPhase })
    const idx = PHASE_ORDER.indexOf(currentPhase)
    if (idx < PHASE_ORDER.length - 1) {
      const next = PHASE_ORDER[idx + 1]
      dispatch({ type: 'SET_PHASE', phase: next })

      // Auto-generate greeting when entering voice phase
      if (next === 'voice' && !state.greetingManuallyEdited) {
        dispatch({
          type: 'SET_GREETING',
          greeting: generateGreeting(state.agentName, state.businessName),
        })
      }
    } else if (mode === 'configure') {
      // In configure mode, save config and close after the last phase (voice)
      handleSaveConfig()
    }
  }, [dispatch, state.agentName, state.businessName, state.greetingManuallyEdited, generateGreeting, PHASE_ORDER, mode])

  const goBack = useCallback(() => {
    const idx = PHASE_ORDER.indexOf(state.phase)
    if (idx > 0) {
      dispatch({ type: 'SET_PHASE', phase: PHASE_ORDER[idx - 1] })
    }
  }, [dispatch, state.phase, PHASE_ORDER])

  const handleSaveConfig = useCallback(async () => {
    dispatch({ type: 'SET_CREATING', creating: true })
    try {
      await api.put('/api/voice/config', {
        voiceId: state.selectedVoiceId,
        tonePreset: state.tonePreset,
        greetingName: state.businessName,
        practiceAreas: state.practiceAreas,
        agentName: state.agentName,
        wizardCompleted: true,
        wizardResponses: state.wizardResponses,
        firmTimezone: state.firmTimezone,
        businessHours: state.businessHours,
        officePhone: state.officePhone,
        primaryLanguage: state.primaryLanguage,
        additionalLanguages: state.additionalLanguages,
        afterHoursMessage: state.afterHoursMessage,
        bookingUrl: state.bookingUrl,
      })
      toast({ title: 'Settings saved', description: 'Agent configuration updated. Prompts will regenerate.' })
      onCreated()
      onClose()
    } catch (err: any) {
      dispatch({ type: 'SET_ERROR', error: err.response?.data?.error || 'Failed to save config.' })
    } finally {
      dispatch({ type: 'SET_CREATING', creating: false })
    }
  }, [state, toast, onCreated, onClose, dispatch])

  const handleCreate = useCallback(async () => {
    const success = await createAgents()
    if (success) {
      toast({ title: 'AI team created', description: 'Your inbound and outbound agents are ready.' })
      onCreated()
      onClose()
    }
  }, [createAgents, toast, onCreated, onClose])

  const currentPhaseIdx = PHASE_ORDER.indexOf(state.phase)

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b flex-shrink-0">
          <SheetTitle>{mode === 'configure' ? 'Edit Agent Wizard' : 'Agent Builder'}</SheetTitle>
          <SheetDescription>
            {mode === 'configure'
              ? 'Update your firm details, voice, and tone — agents will regenerate their prompts'
              : 'Build your inbound and outbound AI agents'}
          </SheetDescription>

          {/* Progress dots */}
          <div className="flex items-center gap-1 pt-2">
            {PHASE_ORDER.map((phase, i) => {
              const isCompleted = state.completedPhases.has(phase)
              const isCurrent = state.phase === phase
              return (
                <div key={phase} className="flex items-center">
                  <button
                    className={`w-2 h-2 rounded-full transition-colors ${
                      isCurrent ? 'w-6 bg-brand-royal' :
                      isCompleted ? 'bg-green-500' :
                      'bg-muted'
                    }`}
                    onClick={() => (isCompleted || isCurrent) && goToPhase(phase)}
                    title={PHASE_LABELS[phase]}
                  />
                  {i < PHASE_ORDER.length - 1 && (
                    <div className={`w-4 h-px mx-0.5 ${i < currentPhaseIdx ? 'bg-green-500' : 'bg-muted'}`} />
                  )}
                </div>
              )
            })}
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1 px-6 py-6">
          <div className="max-w-lg mx-auto">
            {/* Back button */}
            {currentPhaseIdx > 0 && (
              <Button variant="ghost" size="sm" onClick={goBack} className="mb-3 -ml-2 text-muted-foreground">
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            )}

            {/* Completed phase summaries */}
            {PHASE_ORDER.map((phase) => {
              if (!state.completedPhases.has(phase) || state.phase === phase) return null
              return (
                <button
                  key={phase}
                  className="w-full text-left p-3 rounded-lg border bg-muted/30 mb-3 hover:bg-muted/50 transition-colors"
                  onClick={() => goToPhase(phase)}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">{PHASE_LABELS[phase]}</span>
                    <span className="text-xs text-brand-royal">Edit</span>
                  </div>
                  <p className="text-sm mt-0.5 truncate">
                    {phase === 'url' && state.websiteUrl}
                    {phase === 'discovery' && `${state.businessName} — ${state.practiceAreas.length} practice areas`}
                    {phase === 'questions' && `${Object.values(state.wizardResponses).filter(v => v.trim()).length}/5 answered`}
                    {phase === 'operations' && (
                      [
                        state.firmTimezone && COMMON_TZ_LABELS[state.firmTimezone],
                        state.businessHours,
                        state.bookingUrl && 'Booking link set',
                        state.additionalLanguages.length > 0 && `+${state.additionalLanguages.join(', ')}`,
                      ].filter(Boolean).join(' · ') || 'Defaults'
                    )}
                    {phase === 'knowledge' && 'Auto-trained from your website'}
                    {phase === 'voice' && 'Premium Voice'}
                    {phase === 'phone' && (state.selectedPhoneId ? 'Number assigned' : 'Skipped')}
                  </p>
                </button>
              )
            })}

            {/* Active phase */}
            {state.phase === 'url' && (
              <BuilderPhaseUrl
                websiteUrl={state.websiteUrl}
                scrapeStatus={state.scrapeStatus}
                preloaded={state.scrapeStatus === 'preloaded'}
                onAnalyze={(url) => {
                  startScrape(url)
                  dispatch({ type: 'SET_PHASE', phase: 'discovery' })
                }}
                onSkip={() => advancePhase('url')}
                onContinueWithExisting={() => {
                  dispatch({ type: 'COMPLETE_PHASE', phase: 'url' })
                  dispatch({ type: 'SET_PHASE', phase: 'discovery' })
                }}
              />
            )}

            {state.phase === 'discovery' && (
              <BuilderPhaseDiscovery
                scrapeStatus={state.scrapeStatus}
                businessName={state.businessName}
                practiceAreas={state.practiceAreas}
                businessDescription={state.businessDescription}
                contactInfo={state.contactInfo}
                onUpdateName={(name) => dispatch({ type: 'SET_BUSINESS_NAME', name })}
                onUpdateAreas={(areas) => dispatch({ type: 'SET_PRACTICE_AREAS', areas })}
                onUpdateDescription={(desc) => dispatch({ type: 'SET_DESCRIPTION', description: desc })}
                onContinue={() => advancePhase('discovery')}
              />
            )}

            {state.phase === 'questions' && (
              <BuilderPhaseQuestions
                wizardResponses={state.wizardResponses}
                onUpdate={(field, value) => dispatch({ type: 'SET_WIZARD_RESPONSE', field, value })}
                onContinue={() => advancePhase('questions')}
                onSkipAll={() => advancePhase('questions')}
                saving={state.creating}
              />
            )}

            {state.phase === 'operations' && (
              <BuilderPhaseOperations
                firmTimezone={state.firmTimezone}
                businessHours={state.businessHours}
                officePhone={state.officePhone}
                primaryLanguage={state.primaryLanguage}
                additionalLanguages={state.additionalLanguages}
                afterHoursMessage={state.afterHoursMessage}
                bookingUrl={state.bookingUrl}
                onSetTimezone={(tz) => dispatch({ type: 'SET_FIRM_TIMEZONE', timezone: tz })}
                onSetBusinessHours={(hours) => dispatch({ type: 'SET_BUSINESS_HOURS', hours })}
                onSetOfficePhone={(phone) => dispatch({ type: 'SET_OFFICE_PHONE', phone })}
                onSetPrimaryLanguage={(lang) => dispatch({ type: 'SET_PRIMARY_LANGUAGE', language: lang })}
                onSetAdditionalLanguages={(langs) => dispatch({ type: 'SET_ADDITIONAL_LANGUAGES', languages: langs })}
                onSetAfterHoursMessage={(msg) => dispatch({ type: 'SET_AFTER_HOURS_MESSAGE', message: msg })}
                onSetBookingUrl={(url) => dispatch({ type: 'SET_BOOKING_URL', url })}
                onContinue={() => advancePhase('operations')}
                onSkip={() => advancePhase('operations')}
              />
            )}

            {state.phase === 'knowledge' && (
              <BuilderPhaseKnowledge
                knowledgeItems={state.knowledgeItems}
                businessName={state.businessName}
                websiteUrl={state.websiteUrl}
                scrapeStatus={state.scrapeStatus}
                onAddItem={(item) => dispatch({ type: 'ADD_KNOWLEDGE_ITEM', item })}
                onRemoveItem={(id) => dispatch({ type: 'REMOVE_KNOWLEDGE_ITEM', id })}
                onContinue={() => advancePhase('knowledge')}
              />
            )}

            {state.phase === 'voice' && (
              <BuilderPhaseVoice
                voiceOptions={state.voiceOptions}
                selectedVoiceId={state.selectedVoiceId}
                tonePreset={state.tonePreset}
                agentName={state.agentName}
                greeting={state.greeting}
                voicesLoading={state.voicesLoading}
                isLastPhase={mode === 'configure'}
                saving={state.creating}
                onSelectVoice={(id) => {
                  dispatch({ type: 'SET_VOICE', voiceId: id })
                  // Keep the user's chosen agent name — voice selection doesn't change it
                  if (!state.greetingManuallyEdited) {
                    dispatch({ type: 'SET_GREETING', greeting: generateGreeting(state.agentName, state.businessName) })
                  }
                }}
                onSelectTone={(tone) => dispatch({ type: 'SET_TONE', tone })}
                onSetAgentName={(name) => {
                  dispatch({ type: 'SET_AGENT_NAME', name })
                  if (!state.greetingManuallyEdited) {
                    dispatch({ type: 'SET_GREETING', greeting: generateGreeting(name, state.businessName) })
                  }
                }}
                onSetGreeting={(greeting, manual) => dispatch({ type: 'SET_GREETING', greeting, manual })}
                onContinue={() => advancePhase('voice')}
              />
            )}

            {state.phase === 'phone' && (
              <BuilderPhasePhone
                phoneNumbers={state.phoneNumbers}
                selectedPhoneId={state.selectedPhoneId}
                onSelectPhone={(id) => dispatch({ type: 'SET_PHONE', phoneId: id })}
                onContinue={() => advancePhase('phone')}
                onSkip={() => advancePhase('phone')}
              />
            )}

            {state.phase === 'review' && (
              <BuilderPhaseReview
                businessName={state.businessName}
                practiceAreas={state.practiceAreas}
                selectedVoiceId={state.selectedVoiceId}
                tonePreset={state.tonePreset}
                agentName={state.agentName}
                greeting={state.greeting}
                selectedPhoneId={state.selectedPhoneId}
                phoneNumbers={state.phoneNumbers}
                creating={state.creating}
                error={state.error}
                onCreate={handleCreate}
                firmTimezone={state.firmTimezone}
                businessHours={state.businessHours}
                bookingUrl={state.bookingUrl}
                additionalLanguages={state.additionalLanguages}
              />
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}
